import { useMemo, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { SEO } from '../seo/SEO';
import { useI18n } from '../i18n';
import { ROUTE_SLUGS } from '../i18n/routes';
import { useProgress } from '../hooks/useProgress';
import { useAuth } from '../auth';
import { useRoutes } from '../hooks/useRoutes';
import { TechniqueBoard } from '../components/TechniqueBoard';
import { TechniquePractice } from '../components/TechniquePractice';
import { TECHNIQUE_BOARDS } from '../data/technique-boards';
import { AppHeader } from '../components/AppHeader';
import { enLocale, TECHNIQUES, type TechniqueInfo, type DifficultyTier } from '@sudoku/shared';
import { DIFF_COLORS } from '../theme/difficulty';
import './EducationPage.css';

export { TECHNIQUES };
export type { TechniqueInfo };

/**
 * Format SEO title for technique detail pages.
 * If locale is 'en' or localized name matches English name (case/trim insensitive),
 * omits the redundant parenthesized English name to avoid 'Name (Name)' duplication.
 * For non-English locales with distinct English names, preserves 'LocalName (EnglishName)'.
 */
export function formatTechniqueTitle(
  techNameLoc: string,
  techNameEn: string,
  guideSuffix: string,
  locale: string,
): string {
  const isEn = locale === 'en';
  const isSame = techNameLoc.trim().toLowerCase() === techNameEn.trim().toLowerCase();
  if (isEn || isSame) {
    return `${techNameLoc} - ${guideSuffix} | Sudoku Area`;
  }
  return `${techNameLoc} (${techNameEn}) - ${guideSuffix} | Sudoku Area`;
}

// NOT: name/nameEn, summary/summaryEn, explanation/explanationEn, example/exampleEn
// alan-çiftleri kaldırıldı — tüm metin artık shared/src/i18n/{locale}.json'daki
// `edu.techniques.<id>.*` key'lerinden geliyor (N-dil desteği için). Bu array
// sadece YAPISAL veriyi (id, seviye) tutuyor. Zorluk = oyun seviyesi (1-5),
// etiket i18n difficulties[level] — solver/oyun ile TEK ÖLÇEK.
const DIFFICULTY_ORDER: DifficultyTier[] = [1, 2, 3, 4, 5];
const DIFF_KEY: Record<number, string> = {
  1: 'temel', 2: 'orta', 3: 'zor', 4: 'cok-zor', 5: 'asiri',
};
const diffLabel = (t: any, level: DifficultyTier | 'all'): string =>
  level === 'all' ? t.edu.allFilter : (t.difficulties as Record<string, string>)[String(level)];


export function EducationPage() {
  const { nav } = useRoutes();
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const { completedIds, viewedIds, markViewed, markCompleted } = useProgress();
  const { technique: techniqueParam } = useParams<{ technique?: string }>();
  const [filter, setFilter] = useState<'all' | DifficultyTier>('all');
  const [search, setSearch] = useState('');
  const [tutorialCompleted] = useState(() => {
    try { return localStorage.getItem('tutorial_completed_v1') === '1'; } catch { return false; }
  });

  // ScrollTop when technique changes
  useEffect(() => {
    if (techniqueParam) {
      window.scrollTo(0, 0);
    }
  }, [techniqueParam]);

  const selectedTechnique = techniqueParam
    ? TECHNIQUES.find(t => t.id === techniqueParam)
    : null;

  const normalize = (text: string) => {
    const value = text ?? '';
    try {
      return value.toLocaleLowerCase(locale === 'tr' ? 'tr-TR' : 'en-US');
    } catch {
      // Fallback for environments with limited locale support.
      return value.toLowerCase();
    }
  };
  const searchTerm = normalize(search.trim());

  const filteredTechniques = useMemo(() => {
    const base = TECHNIQUES.filter((tech) => {
      if (filter !== 'all' && tech.level !== filter) return false;
      if (!searchTerm) return true;

      const tx = (t.edu.techniques as any)[tech.id];
      const haystack = [tx?.name ?? '', tx?.summary ?? ''].map(normalize);

      return haystack.some((value) => value.includes(searchTerm));
    });

    return base.sort((a, b) => {
      const d = a.level - b.level;
      if (d !== 0) return d;
      const aName = (t.edu.techniques as any)[a.id]?.name ?? '';
      const bName = (t.edu.techniques as any)[b.id]?.name ?? '';
      return aName.localeCompare(bName, locale === 'tr' ? 'tr' : 'en');
    });
  }, [filter, searchTerm, locale, t]);

  const grouped = DIFFICULTY_ORDER.map(level => ({
    level,
    techniques: filteredTechniques.filter(t => t.level === level),
  })).filter(g => g.techniques.length > 0);

  // Mark technique as viewed when detail page is opened (must be before early returns)
  useEffect(() => {
    if (selectedTechnique) markViewed(selectedTechnique.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTechnique?.id]);

  // If technique param exists but technique not found, show error
  if (techniqueParam && !selectedTechnique) {
    return (
      <div className="education-page">
        <AppHeader onBack={() => nav('education')} backLabel={t.edu.backToTechniques} title={t.edu.notFoundTitle} />
        <p style={{ margin: '2rem 0', color: 'var(--text-secondary)' }}>
          {t.edu.notFoundMessage.replace('{id}', techniqueParam ?? '')}
        </p>
      </div>
    );
  }

  if (selectedTechnique) {
    const tx = (t.edu.techniques as any)[selectedTechnique.id];
    const summary: string = tx.summary;
    const explanation: string = tx.explanation;

    const formatCellRef = (text: string) =>
      locale === 'tr'
        ? text.replace(/R(\d+)C(\d+)/g, '$1. satır $2. sütun')
        : text;
    const boardExample = TECHNIQUE_BOARDS[selectedTechnique.id];

    interface EduTechniqueItem {
      name: string;
    }
    interface EduData {
      techniques: Record<string, EduTechniqueItem>;
      techniqueGuideSuffix?: string;
    }
    const eduDict = t.edu as unknown as EduData;
    const eduEnDict = enLocale.edu as unknown as EduData;
    const techNameLoc: string = tx.name;
    const techNameEn: string = eduEnDict.techniques?.[selectedTechnique.id]?.name ?? techNameLoc;
    const guideSuffix = eduDict.techniqueGuideSuffix ?? 'Sudoku Technique Guide';
    const seoTitle = formatTechniqueTitle(techNameLoc, techNameEn, guideSuffix, locale);
    const seoDesc = summary;

    // Breadcrumb JSON-LD
    const SITE = 'https://sudokuarea.com';
    const eduSlug = ROUTE_SLUGS[locale].education;
    const homeSlug = ROUTE_SLUGS[locale].home;
    const breadcrumbJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: t.common.home, item: `${SITE}${homeSlug}` },
        { '@type': 'ListItem', position: 2, name: t.edu.title, item: `${SITE}${eduSlug}` },
        { '@type': 'ListItem', position: 3, name: techNameLoc, item: `${SITE}${eduSlug}/${selectedTechnique.id}` },
      ],
    };

    return (
      <div className="education-page">
        <SEO
          path="/education"
          title={seoTitle}
          description={seoDesc}
          pathSuffix={`/${selectedTechnique.id}`}
          extraJsonLd={breadcrumbJsonLd}
        />
        <AppHeader onBack={() => nav('education')} backLabel={t.edu.backToTechniques} title={techNameLoc} />

        <div className="technique-detail">
          <div className="tech-meta">
            {locale !== 'en' && <span className="tech-badge-en">{techNameEn}</span>}
            <span
              className="tech-badge-diff"
              style={{ background: DIFF_COLORS[selectedTechnique.level] }}
            >
              {diffLabel(t, selectedTechnique.level)}
            </span>
          </div>

          <div className="tech-summary">
            <p>{summary}</p>
          </div>

          <div className="tech-section">
            <h2>{t.edu.howItWorks}</h2>
            {explanation.split('\n\n').map((para, i) => (
              <p key={i}>{formatCellRef(para)}</p>
            ))}
          </div>

          <div className="tech-section">
            <h2>{t.edu.example}</h2>
            {boardExample ? (
              <TechniqueBoard
                data={boardExample.data}
              />
            ) : (
              <p className="tech-board-unavailable">{t.edu.boardUnavailable}</p>
            )}
          </div>

          {/* whenToUse/howToSpot/whichCandidates/commonMistake/exercise alanları
              hiçbir teknik için doldurulmamıştı (ölü kod) — N-dil migrasyonunda
              kaldırıldı. İçerik eklenirse i18n key olarak edu.techniques.<id>
              altına eklenmeli. */}

          <TechniquePractice slug={selectedTechnique.id} />

          {user && (
            <div className="tech-section tech-complete-section">
              {completedIds.has(selectedTechnique.id) ? (
                <span className="tech-completed-badge">{t.edu.lessonCompleted}</span>
              ) : (
                <button
                  className="btn-complete"
                  onClick={() => { markCompleted(selectedTechnique.id); nav('education'); }}
                >
                  {t.edu.markCompletedBtn}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="education-page">
      <SEO path="/education" />
      <AppHeader onBack={() => nav('home')} backLabel={t.common.backToHome} title={t.edu.title} />

      <p className="edu-intro">
        {t.edu.intro}
      </p>

      {user && completedIds.size > 0 && (
        <div className="edu-progress-bar-wrap">
          <div className="edu-progress-bar">
            <div
              className="edu-progress-fill"
              style={{ width: `${Math.round((completedIds.size / TECHNIQUES.length) * 100)}%` }}
            />
          </div>
          <span className="edu-progress-label">
            {completedIds.size}/{TECHNIQUES.length} {t.edu.progressSummary}
          </span>
        </div>
      )}

      <button type="button" className="edu-tutorial-entry" onClick={() => nav('tutorial')}>
        <span className="edu-tutorial-entry__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M9 3v18M15 3v18M3 9h18M3 15h18" />
          </svg>
        </span>
        <span className="edu-tutorial-entry__text">
          <strong>{t.profile.help.tutorialTitle}</strong>
          <span>{t.profile.help.tutorialDesc}</span>
        </span>
        <span className="edu-tutorial-entry__action">
          {tutorialCompleted ? t.profile.replay : t.profile.help.tutorialBtn}
        </span>
        <svg className="edu-tutorial-entry__chevron" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>

      <div className="edu-controls">
        <div className="edu-search-row">
          <input
            className="edu-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.edu.searchPlaceholder}
          />
          {search && (
            <button className="edu-clear-btn" onClick={() => setSearch('')}>
              {t.edu.clearSearch}
            </button>
          )}
        </div>
      </div>

      <div className="filter-bar">
        {(['all', ...DIFFICULTY_ORDER] as const).map(d => (
          <button
            key={d}
            className={`filter-btn ${filter === d ? 'active' : ''}`}
            style={filter === d && d !== 'all' ? { background: DIFF_COLORS[d], color: 'white' } : {}}
            onClick={() => setFilter(d)}
          >
            {diffLabel(t, d)}
          </button>
        ))}
      </div>

      <div className="technique-count">
        {`${filteredTechniques.length} ${t.edu.techniqueCount}`}
      </div>

      {filteredTechniques.length === 0 && (
        <div className="edu-empty-state">{t.edu.emptyState}</div>
      )}

      {filteredTechniques.length > 0 && (
        grouped.map(group => (
            <div key={group.level} className="tech-group">
              <h2 className="group-title" style={{ color: DIFF_COLORS[group.level] }}>
                {diffLabel(t, group.level)}
              </h2>
              <div className="tech-grid">
                {group.techniques.map(tech => {
                  const ttx = (t.edu.techniques as any)[tech.id];
                  const enName = (enLocale as any).edu.techniques[tech.id]?.name ?? ttx?.name;
                  return (
                    <div
                      key={tech.id}
                      className={`tech-card tech-card--${DIFF_KEY[tech.level]}${completedIds.has(tech.id) ? ' tech-card--completed' : viewedIds.has(tech.id) ? ' tech-card--viewed' : ''}`}
                      onClick={() => nav('education', tech.id)}
                    >
                      <span
                        className="tech-diff-dot"
                        style={{ background: DIFF_COLORS[tech.level] }}
                      />
                      {completedIds.has(tech.id) && <span className="tech-check">✓</span>}
                      <span className="tech-name">{ttx?.name}</span>
                      {locale !== 'en' && <span className="tech-card-en">{enName}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
      )}
    </div>
  );
}
